import { useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";

import type { SignupKeyCreatedResponse, UserRole } from "@/api";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDateID } from "@/lib/format";

const ROLE_LABEL: Record<UserRole | string, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  member: "Anggota",
  viewer: "Pengamat",
  partner_admin: "Partner admin",
};

interface Props {
  keyData: SignupKeyCreatedResponse | null;
  onClose: () => void;
}

/** Post-create dialog for signup keys. Modal-locked (no
 *  backdrop-close / Escape) — the plaintext code is irretrievable
 *  once dismissed, so the user MUST acknowledge they've saved it.
 *  Mirrors the API-key show-once UX from Sesi FE-2.
 */
export function SignupKeyShowOnceDialog({ keyData, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!keyData) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(keyData.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — user can still select + copy manually.
    }
  };

  const handleClose = () => {
    setCopied(false);
    setAcknowledged(false);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-lg" modal>
      <DialogHeader>
        <DialogTitle>
          <span className="inline-flex items-center gap-2">
            <Check size={18} className="text-success-600" />
            Signup key dibuat
          </span>
        </DialogTitle>
        <DialogDescription>
          Bagikan kode di bawah ke calon anggota tim — mereka akan
          memakainya saat registrasi. Kode tidak akan ditampilkan lagi
          setelah dialog ditutup.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 px-4 py-3">
          <div className="label-eyebrow mb-2">
            Peran: {ROLE_LABEL[keyData.default_role] ?? keyData.default_role}
          </div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-ocean-100 bg-white px-3 py-2 font-mono text-sm text-ocean-900">
              {keyData.code}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="shrink-0"
              aria-label={copied ? "Kode tersalin" : "Salin kode"}
            >
              {copied ? (
                <>
                  <Check size={14} className="text-success-600" />
                  Tersalin
                </>
              ) : (
                <>
                  <Copy size={14} />
                  Salin
                </>
              )}
            </Button>
          </div>
        </div>

        <Alert variant="warning">
          <div className="flex items-start gap-2 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>Penting:</strong> Kode ini hanya ditampilkan sekali.
              Salin sekarang — Monago tidak menyimpan plaintext dan tidak
              dapat menampilkannya kembali.
            </div>
          </div>
        </Alert>

        <div className="grid grid-cols-1 gap-1 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600 tablet:grid-cols-2">
          <div>
            <span className="font-medium text-neutral-800">Berlaku sampai:</span>{" "}
            {formatDateID(keyData.expires_at)}
          </div>
          {keyData.note && (
            <div>
              <span className="font-medium text-neutral-800">Catatan:</span>{" "}
              {keyData.note}
            </div>
          )}
        </div>

        <label className="flex items-start gap-2 text-xs text-neutral-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ocean-200 text-ocean-600 focus:ring-ocean-400"
          />
          <span>Saya sudah menyalin signup key dan menyimpannya di tempat aman.</span>
        </label>
      </DialogBody>

      <DialogFooter>
        <Button type="button" onClick={handleClose} disabled={!acknowledged}>
          Sudah disimpan, tutup
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
